import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingState } from "../reducer";
import {
  getUserDetails,
  postUserDetails,
  requestAuthenticationChange
} from "../../../api/endpoints/user/user";
import { requestOTPVerfication } from "../../../api/endpoints/user/authentication";
import { getCredentials } from "../../../api/endpoints/user/credentials";

export enum LoginStatus {
  SIGNED_IN = "signedIn",
  SIGNED_OUT = "signedOut",
  PENDING = "pending",
  FAILED = "failed",
  OTP_PENDING = "otpPending"
}

/**
 * Interface representing the deeply nested notification preferences
 */
export interface NotificationPreferences {
  reports: {
    enabled: boolean;
    vulnerability_reports: {
      enabled: boolean;
      severities: {
        critical: boolean;
        high: boolean;
        medium: boolean;
        low: boolean;
        info: boolean;
      };
    };
    comments: { enabled: boolean } | boolean;
    retests: { enabled: boolean } | boolean;
  };
  programs: {
    enabled: boolean;
    invitations_and_activation: { enabled: boolean } | boolean;
    program_activity_and_automation: { enabled: boolean } | boolean;
  };
  platform: {
    enabled: boolean;
    user_management: { enabled: boolean } | boolean;
    platform_security_alerts: { enabled: boolean } | boolean;
  };
  security: { enabled: boolean } | boolean;
  payments?: boolean;
}

/**
 * Interface for user credentials
 */
export interface UserCredentials {
  userId?: number;
  username?: string;
  userRole?: number;
  userEmail?: string;
  accessToken?: string;
  refreshToken?: string;
  mfaType?: string;
}

/**
 * Details of the current user
 */
export interface UserDetails {
  user_id: number;
  username: string;
  email: string;
  role: number;
  country: string;
  pfp: string;
  profilePicture: string; // Alias for UI
  banner: string;
  bannerPicture: string; // Alias for UI
  email_verified: boolean;
  two_fa_enabled: boolean;
  otpEnabled: boolean;
  about: string;
  displayName: string;
  links: string[];
  skills: string[];
  languages: string[];
  hobbies: string[];
  education: string[];
  work_experience: string[];
  achievements: string[];
  community_engagement: string[];
  testimonials: string[];
  publications: string[];
  security_clearance: string[];
  tools_used: string[];
  availability: boolean;
  company_id: number;
  subscription_type: string;
  payment_details: string;
  paymentDetails: any;
  email_notifications_enabled: boolean;
  email_preferences: NotificationPreferences;
  totpSecret?: string | null;
}

export interface SetUserDetails {
  email?: string;
  country?: string;
  pfp?: string;
  banner?: string;
  about?: string;
  displayName?: string;
  profilePicture?: Blob;
  bannerPicture?: Blob;
  links?: string[];
  skills?: string[];
  languages?: string[];
  hobbies?: string[];
  education?: string[];
  work_experience?: string[];
  achievements?: string[];
  community_engagement?: string[];
  testimonials?: string[];
  publications?: string[];
  security_clearance?: string[];
  tools_used?: string[];
  availability?: boolean;
  email_notifications_enabled?: boolean;
  email_preferences?: NotificationPreferences;
}

export interface UserState {
  loginStatus: LoginStatus;
  credentials: {
    username: string;
    userRole: number;
    userId: number;
    userEmail: string;
    status?: LoadingState;
    mfaType?: string;
  };
  details: {
    status: LoadingState;
  } & UserDetails;
}

// Hydrate initial state from localStorage
const savedCredentials = getCredentials();

const initialState: UserState = {
  loginStatus: savedCredentials.accessToken ? LoginStatus.SIGNED_IN : LoginStatus.SIGNED_OUT,
  credentials: {
    username: savedCredentials.username || "",
    userRole: savedCredentials.userRole || 0,
    userId: savedCredentials.userId || 0,
    userEmail: savedCredentials.userEmail || "",
    status: LoadingState.NOT_STARTED
  },
  details: {
    status: LoadingState.NOT_STARTED,
    user_id: savedCredentials.userId || 0,
    username: savedCredentials.username || "",
    email: savedCredentials.userEmail || "",
    role: savedCredentials.userRole || 0,
    country: "",
    pfp: "",
    profilePicture: "",
    banner: "",
    bannerPicture: "",
    email_verified: false,
    two_fa_enabled: false,
    otpEnabled: false,
    about: "",
    displayName: "",
    links: [],
    skills: [],
    languages: [],
    hobbies: [],
    education: [],
    work_experience: [],
    achievements: [],
    community_engagement: [],
    testimonials: [],
    publications: [],
    security_clearance: [],
    tools_used: [],
    availability: false,
    company_id: 0,
    subscription_type: "FREE",
    payment_details: "",
    paymentDetails: null,
    email_notifications_enabled: true,
    email_preferences: {
      reports: {
        enabled: true,
        vulnerability_reports: {
          enabled: true,
          severities: {
            critical: true,
            high: true,
            medium: true,
            low: true,
            info: true
          }
        },
        comments: { enabled: true },
        retests: { enabled: true }
      },
      programs: {
        enabled: true,
        invitations_and_activation: { enabled: true },
        program_activity_and_automation: { enabled: true }
      },
      platform: {
        enabled: true,
        user_management: { enabled: true },
        platform_security_alerts: { enabled: true }
      },
      security: { enabled: true }
    }
  }
};

export const fetchUserDetails = createAsyncThunk(
  "user/fetchDetails",
  async () => {
    return await getUserDetails();
  }
);

export const updateUserDetails = createAsyncThunk(
  "user/updateDetails",
  async (details: { username: string } & SetUserDetails) => {
    return await postUserDetails(details);
  }
);

export const changeAuthentication = createAsyncThunk(
  "user/changeAuthentication",
  async (data: { enabled: boolean; code?: string }) => {
    return await requestAuthenticationChange(data.enabled, data.code);
  }
);

export const verfiyOTP = createAsyncThunk(
  "user/verifyOTP",
  async (data: { email: string; code: string; useFallback?: boolean }) => {
    return await requestOTPVerfication(data.email, data.code, data.useFallback);
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setLoginStatus: (state, action: PayloadAction<LoginStatus>) => {
      state.loginStatus = action.payload;
    },
    setCredentials: (state, action: PayloadAction<UserCredentials>) => {
      state.credentials = {
        ...state.credentials,
        username: action.payload.username || state.credentials.username,
        userRole: action.payload.userRole || state.credentials.userRole,
        userId: action.payload.userId || state.credentials.userId,
        userEmail: action.payload.userEmail || state.credentials.userEmail,
        mfaType: action.payload.mfaType || state.credentials.mfaType
      };
      
      // Update details to match for consistency
      if (action.payload.userId) state.details.user_id = action.payload.userId;
      if (action.payload.username) state.details.username = action.payload.username;
      if (action.payload.userRole) state.details.role = action.payload.userRole;
      if (action.payload.userEmail) state.details.email = action.payload.userEmail;
      
      if (action.payload.accessToken) {
        state.loginStatus = LoginStatus.SIGNED_IN;
      } else if (action.payload.userEmail) {
        state.loginStatus = LoginStatus.OTP_PENDING;
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchUserDetails.pending, state => {
        state.details.status = LoadingState.LOADING;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.details.status = LoadingState.IDLE;
        state.details = {
          ...state.details,
          ...(action.payload as any),
          profilePicture: (action.payload as any).pfp, // Alias
          bannerPicture: (action.payload as any).banner, // Alias
          // Map DB column two_fa_enabled → Redux field otpEnabled
          otpEnabled: !!(action.payload as any).two_fa_enabled || !!(action.payload as any).otpEnabled
        };
        state.credentials = {
          ...state.credentials,
          username: (action.payload as any).username,
          userRole: (action.payload as any).role,
          userId: (action.payload as any).user_id,
          userEmail: (action.payload as any).email
        };
      })
      .addCase(fetchUserDetails.rejected, state => {
        state.details.status = LoadingState.FAILED;
      })
      .addCase(updateUserDetails.fulfilled, (state, action) => {
        state.details = {
          ...state.details,
          ...(action.payload as any)
        };
      })
      .addCase(changeAuthentication.fulfilled, (state, action) => {
        state.details.otpEnabled = action.payload as boolean;
      })
      .addCase(verfiyOTP.fulfilled, (state, action) => {
        if (action.payload && (action.payload as any).accessToken) {
          const payload = action.payload as any;
          state.loginStatus = LoginStatus.SIGNED_IN;
          state.credentials = {
            ...state.credentials,
            username: payload.username || state.credentials.username,
            userRole: payload.userRole || state.credentials.userRole,
            userId: payload.userId || state.credentials.userId,
            userEmail: payload.userEmail || state.credentials.userEmail
          };
          
          // Update details for consistency
          if (payload.userId) state.details.user_id = payload.userId;
          if (payload.username) state.details.username = payload.username;
          if (payload.userRole) state.details.role = payload.userRole;
          if (payload.userEmail) state.details.email = payload.userEmail;
        }
      });
  }
});

export const { setLoginStatus, setCredentials } = userSlice.actions;

export default userSlice.reducer;
